// BRIEF 102 §2d — ONE definition of "what colour is this task's urgency", for
// every surface that puts a task title on screen.
//
// MOVED, NOT REWRITTEN. The body below is `tkClr` as it stood at
// `src/components/CaseTaskRow.tsx:58`, character for character, and that file
// now imports it. §2d's instruction was the whole reason this file exists:
//
//   "Do not write a second copy. Its own header says it was once duplicated per
//    view and that duplication has since been drained — recreating it in a third
//    file would undo that in the same session that fixed `DateBlock` for the
//    same reason."
//
// The header it refers to read "Identical to the per-view tkClr helpers
// (CaseView / CollectiveCaseView)", and those two are gone: a grep across src/
// finds `tkClr` in exactly one file. So the choice was between a third copy and
// a shared definition, and there was nothing to weigh.
//
// ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
// It answers about a TASK's own `status` and `dueDate` and reaches for nothing
// else — no viewer, no delegation, no subtask. `viewerDueDate` (src/lib/
// dueToday.ts) is the function that knows whose date a row is showing, and a
// caller that renders a viewer-scoped date is asking a different question.
//
// The `toISOString().slice(0, 10)` below is UTC and `src/lib/localToday.ts`
// exists because that is wrong near midnight. It is preserved exactly as it
// was: this commit moves a definition and changes no answer. Recorded in the
// PR's "surfaced, not fixed" instead.

// ── AND THE `any`s DID NOT COME WITH IT ─────────────────────────────────────
// The body arrived from a `.tsx` written before this tree had types, carrying
// `(t: any)` and two `as any` casts around date subtraction. `scripts/lint/
// ratchets.js` refused it — "NEW file may add none" — and it is right to: the
// re-record it offers is for a file MOVE, and this is three lines lifted out of
// one. So the arithmetic is the same and the types are real. `getTime()` is
// what the `as any` casts were spelling.

/** Only what this answer depends on. Both fields are optional because a task
 *  row may hold neither, and `dueDate` is whatever the API handed the client. */
type UrgencyTask = {
  status?: string | null;
  dueDate?: string | Date | null;
};

const DAY_MS = 1000 * 60 * 60 * 24;

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * `--tm` archived/cancelled · `--gn` completed · `--rd` overdue ·
 * `--am` due within seven days · `undefined` otherwise (the caller's own
 * colour, which is why this returns undefined rather than a token).
 */
export const taskUrgencyColor = (t: UrgencyTask): string | undefined => {
  if (t.status === "cancelled" || t.status === "archived") return "var(--tm)";
  if (t.status === "completed") return "var(--gn)";
  if (!t.dueDate) return undefined;
  const diff = Math.round(
    (new Date(t.dueDate).getTime() - new Date(todayISO()).getTime()) / DAY_MS,
  );
  return diff < 0 ? "var(--rd)" : diff <= 7 ? "var(--am)" : undefined;
};
